import { CheckCircle2, ShoppingCart, Sparkles } from 'lucide-react';
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { usePermission } from '../../hooks/usePermission.js';
import { useAuthStore } from '../../lib/authStore.js';
import {
  type DisplaySnapshot,
  readLastSnapshot,
  subscribeDisplay,
} from '../../lib/customerDisplay.js';
import { formatMoney } from '../../lib/money.js';
import { ItemThumb } from '../items/ItemDetailDialog.js';

/**
 * Customer-facing second screen (the `customer_display` module). Opened on a
 * second monitor from the POS register; mirrors the live cart in real time via
 * BroadcastChannel. Read-only — no API calls, no chrome. Shows a branded idle
 * screen when there's no active sale and a thank-you on finalize.
 */
export function CustomerDisplayPage(): JSX.Element {
  const { t } = useTranslation('pos');
  const tenantName = useAuthStore((s) => s.tenant?.name ?? '');
  const canImages = usePermission('attachments.read');
  const [snap, setSnap] = useState<DisplaySnapshot | null>(() => readLastSnapshot());

  useEffect(() => subscribeDisplay(setSnap), []);

  const locale = snap?.locale ?? 'en';
  const currency = snap?.currency ?? 'USD';
  const company = snap?.company || tenantName;
  const money = (v: string): string => formatMoney(v, { locale, currency });
  const dir = locale.startsWith('ar') ? 'rtl' : 'ltr';
  const state = snap?.state ?? 'idle';

  return (
    <div
      dir={dir}
      lang={locale}
      className="flex h-[var(--vh100)] w-full flex-col overflow-hidden bg-gradient-to-br from-bg via-bg to-brand/15 text-fg"
    >
      {/* brand header */}
      <header className="flex flex-none items-center justify-between gap-3 border-b border-border/60 px-4 py-3 sm:px-8 sm:py-5">
        <div className="flex min-w-0 items-center gap-2.5 sm:gap-3">
          <span className="grid h-10 w-10 flex-none place-items-center rounded-xl bg-gradient-to-br from-brand to-accent text-brand-contrast shadow-lg sm:h-12 sm:w-12 sm:rounded-2xl">
            <ShoppingCart className="h-5 w-5 sm:h-6 sm:w-6" aria-hidden />
          </span>
          <div className="min-w-0">
            <div className="truncate text-lg font-extrabold tracking-tight sm:text-2xl">{company}</div>
            <div className="truncate text-xs text-fg-muted sm:text-sm">
              {t('display.tagline', { defaultValue: 'Welcome — thank you for shopping with us' })}
            </div>
          </div>
        </div>
        {snap?.customerName && (
          <div className="max-w-[40%] flex-none truncate rounded-full border border-border bg-bg-subtle/60 px-3 py-1 text-sm font-semibold sm:px-4 sm:py-1.5 sm:text-base">
            {snap.customerName}
          </div>
        )}
      </header>

      {/* body */}
      {state === 'idle' || !snap || snap.lines.length === 0 ? (
        <IdleScreen company={company} t={t} />
      ) : (
        <div className="flex min-h-0 flex-1 flex-col">
          {/* line items */}
          <div className="min-h-0 flex-1 overflow-y-auto px-3 py-4 sm:px-8 sm:py-6">
            <ul className="mx-auto flex max-w-4xl flex-col gap-2 sm:gap-3">
              {snap.lines.map((l) => (
                <li
                  key={l.id}
                  className="flex items-center gap-3 rounded-xl border border-border bg-bg-subtle/40 px-3 py-3 shadow-sm duration-300 animate-in fade-in slide-in-from-bottom-2 sm:gap-4 sm:rounded-2xl sm:px-5 sm:py-4"
                >
                  {/* product image with a quantity badge */}
                  <div className="relative flex-none">
                    <ItemThumb
                      itemId={l.itemId}
                      seed={l.itemId ?? l.name}
                      enabled={canImages}
                      className="h-12 w-12 sm:h-16 sm:w-16"
                    />
                    <span className="absolute -end-1.5 -top-1.5 grid h-6 min-w-[1.5rem] place-items-center rounded-full bg-brand px-1.5 text-xs font-bold tabular-nums text-brand-contrast ring-2 ring-bg sm:h-7 sm:min-w-[1.75rem] sm:text-sm">
                      {trimQty(l.quantity)}
                    </span>
                  </div>
                  <div className="min-w-0 flex-1">
                    <div className="truncate text-base font-semibold sm:text-xl">{l.name}</div>
                    <div className="truncate text-xs text-fg-muted sm:text-sm">
                      {money(l.unitPrice)}
                      {l.unitName ? ` · ${l.unitName}` : ''}
                    </div>
                  </div>
                  <span className="font-mono text-base font-bold tabular-nums sm:text-xl">{money(l.lineTotal)}</span>
                </li>
              ))}
            </ul>
          </div>

          {/* totals bar */}
          <div className="flex-none border-t border-border bg-bg-subtle/60 px-4 py-4 backdrop-blur sm:px-8 sm:py-6">
            <div className="mx-auto flex max-w-4xl flex-col gap-1.5 sm:gap-2">
              <SummaryRow label={t('sale.subtotal')} value={money(snap.subtotal)} muted />
              {Number(snap.taxTotal) > 0 && (
                <SummaryRow label={t('sale.tax')} value={money(snap.taxTotal)} muted />
              )}
              {Number(snap.discountTotal) > 0 && (
                <SummaryRow
                  label={t('sale.discount')}
                  value={`−${money(snap.discountTotal)}`}
                  tone="success"
                />
              )}
              <div className="mt-1 flex items-baseline justify-between gap-3 border-t border-border pt-2 sm:pt-3">
                <span className="text-lg font-bold uppercase tracking-wide sm:text-2xl">{t('sale.total')}</span>
                <span
                  key={snap.total}
                  className="font-mono text-3xl font-extrabold tabular-nums text-brand duration-200 animate-in fade-in zoom-in-95 sm:text-5xl"
                >
                  {money(snap.total)}
                </span>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* finalize overlay */}
      {state === 'done' && snap && (
        <div className="absolute inset-0 z-10 flex flex-col items-center justify-center gap-4 bg-bg/95 px-4 text-center backdrop-blur duration-300 animate-in fade-in sm:gap-6">
          <span className="grid h-20 w-20 place-items-center rounded-full bg-success/15 text-success ring-4 ring-success/20 duration-500 animate-in zoom-in-50 sm:h-28 sm:w-28 sm:ring-8">
            <CheckCircle2 className="h-12 w-12 sm:h-16 sm:w-16" aria-hidden />
          </span>
          <div className="text-2xl font-bold sm:text-3xl">{t('display.thankYou', { defaultValue: 'Thank you!' })}</div>
          <div className="font-mono text-4xl font-extrabold tabular-nums sm:text-6xl">{money(snap.total)}</div>
          {snap.changeDue && Number(snap.changeDue) > 0 && (
            <div className="text-lg sm:text-2xl">
              <span className="text-fg-muted">{t('sale.changeDue')}: </span>
              <span className="font-mono font-bold text-success">{money(snap.changeDue)}</span>
            </div>
          )}
        </div>
      )}
    </div>
  );
}

function IdleScreen({
  company,
  t,
}: {
  company: string;
  t: ReturnType<typeof useTranslation>['t'];
}): JSX.Element {
  return (
    <div className="flex flex-1 flex-col items-center justify-center gap-4 px-6 text-center sm:gap-6 sm:px-8">
      <span className="grid h-24 w-24 place-items-center rounded-full bg-gradient-to-br from-brand/20 to-accent/15 text-brand ring-1 ring-brand/20 sm:h-32 sm:w-32">
        <Sparkles className="h-12 w-12 sm:h-16 sm:w-16" aria-hidden />
      </span>
      <div className="text-3xl font-extrabold tracking-tight sm:text-4xl">
        {t('display.welcome', { defaultValue: 'Welcome' })}
      </div>
      <div className="max-w-xl text-base text-fg-muted sm:text-xl">
        {t('display.idleHint', {
          company,
          defaultValue: 'Your items will appear here as they are scanned.',
        })}
      </div>
    </div>
  );
}

function SummaryRow({
  label,
  value,
  muted,
  tone,
}: {
  label: string;
  value: string;
  muted?: boolean;
  tone?: 'success';
}): JSX.Element {
  return (
    <div className="flex items-baseline justify-between text-sm sm:text-lg">
      <span className={muted ? 'text-fg-muted' : 'text-fg'}>{label}</span>
      <span
        className={
          'font-mono tabular-nums ' + (tone === 'success' ? 'text-success' : 'text-fg')
        }
      >
        {value}
      </span>
    </div>
  );
}

/** Trim trailing zeros for a clean quantity (e.g. "2.0000" → "2"). */
function trimQty(v: string): string {
  const n = Number(v);
  return Number.isFinite(n) ? String(n) : v;
}
